import {
  Box,
  Modal as ChakraModal,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalCloseButtonProps,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  ModalProps,
} from '@chakra-ui/react';
import { IconInfo } from 'icons';

const modalVariantColors = {
  green: {
    overlayBg: 
      'linear-gradient(90deg, rgba(79, 197, 186, 0.2) 0%, rgba(79, 197, 186, 0) 50%)',
    iconBg: 
      'linear-gradient(302.62deg, rgba(0, 255, 232, 0.06) 0%, rgba(0, 255, 232, 0.12) 50%, rgba(0, 255, 232, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(0, 255, 232, 0.6) 0.87%, rgba(0, 255, 232, 0) 30.35%, rgba(0, 255, 232, 0) 69.65%, rgba(0, 255, 232, 0.6) 99.13%)',
  },
  blue: {
    overlayBg:
      'linear-gradient(90deg, rgba(42, 102, 255, 0.2) 0%, rgba(42, 102, 255, 0) 50%)',
    iconBg:
      'linear-gradient(302.62deg, rgba(42, 102, 255, 0.06) 0%, rgba(42, 102, 255, 0.12) 50%, rgba(42, 102, 255, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(42, 102, 255, 0.6) 0.87%, rgba(42, 102, 255, 0) 30.35%, rgba(42, 102, 255, 0) 69.65%, rgba(42, 102, 255, 0.6) 99.13%)',    
  },
  red: {
    overlayBg: 
      'linear-gradient(90deg, rgba(255, 0, 17, 0.2) 0%, rgba(255, 0, 17, 0) 50%)',
    iconBg:
      'linear-gradient(302.62deg, rgba(249, 57, 57, 0.06) 0%, rgba(249, 57, 57, 0.12) 50%, rgba(249, 57, 57, 0.06) 100%)',

    iconBorderColor:
      'linear-gradient(91.01deg, rgba(249, 57, 57, 0.6) 0.87%, rgba(249, 57, 57, 0) 30.35%, rgba(249, 57, 57, 0) 69.65%, rgba(249, 57, 57, 0.6) 99.13%)',
  },
  golden: {
    overlayBg:
      'linear-gradient(90deg, rgba(255, 221, 86, 0.2) 0%, rgba(255, 221, 86, 0) 50%)',
    iconBg:
      'linear-gradient(304.94deg, rgba(255, 204, 0, 0.06) 0%, rgba(255, 204, 0, 0.12) 50%, rgba(255, 204, 0, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(229, 186, 85, 0.6) 0.87%, rgba(229, 186, 85, 0) 30.35%, rgba(229, 186, 85, 0) 69.65%, rgba(229, 186, 85, 0.6) 99.13%)',  
  },
  grey: {
    overlayBg:
      'linear-gradient(90deg, rgba(215, 215, 215, 0.2) 0%, rgba(215, 215, 215, 0) 50%)',
    iconBg:
      'linear-gradient(302.62deg, rgba(224, 224, 224, 0.06) 0%, rgba(224, 224, 224, 0.12) 50%, rgba(224, 224, 224, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(224, 224, 224, 0.6) 0.87%, rgba(224, 224, 224, 0) 30.35%, rgba(224, 224, 224, 0) 69.65%, rgba(224, 224, 224, 0.6) 99.13%)',
  },
  ftue: {
    overlayBg:
      'linear-gradient(360deg, rgba(50, 170, 56, 0.2) 0%, rgba(50, 170, 56, 0) 30%, rgba(42, 102, 255, 0) 70%, rgba(42, 102, 255, 0.2) 100%)',
    iconBg:
      'linear-gradient(302.62deg, rgba(42, 102, 255, 0.06) 0%, rgba(42, 102, 255, 0.12) 50%, rgba(42, 102, 255, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(42, 102, 255, 0.6) 0.87%, rgba(42, 102, 255, 0) 30.35%, rgba(42, 102, 255, 0) 69.65%, rgba(42, 102, 255, 0.6) 99.13%)',    
  },
}

const Modal = ({
  title,
  children,
  closeButtonProps,
  footer,
  modalVariant = 'green',
  icon = <IconInfo />,
  copyButton,
  showCloseButton = true,
  ...rest
}: ModalProps & {
  title: string;
  closeButtonProps?: ModalCloseButtonProps;
  footer?: React.ReactNode;
  modalVariant?: 'green' | 'blue' | 'red' | 'grey' | 'golden' | 'ftue';
  icon?: React.ReactNode;
  copyButton?: React.ReactNode;
  showCloseButton?: boolean;
}) => {
  return (
    <ChakraModal isCentered {...rest}>
      <ModalOverlay />
      <ModalContent p={0}>
        <Box 
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg={modalVariantColors[modalVariant].overlayBg}
          borderRadius="8px"
          zIndex={-1}
        />
        <ModalHeader>
          <Flex
            position="relative"
            p={{ base: '1', md: '1.5' }}
            mr={{ base: '0.5rem', md: '1rem' }}
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: modalVariantColors[modalVariant].iconBorderColor,
              borderRadius: '4px',
              padding: '2px',
              mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              maskComposite: 'exclude',
            }}
          >
            <Flex
              background={modalVariantColors[modalVariant].iconBg}
              borderRadius="4px"
              px={{ base: '0.75rem', md: '1rem' }}
              py={{ base: '0.4rem', md: '0.5rem' }}
            >
              {icon}
            </Flex>
          </Flex>
          {copyButton && (
            <Flex>
              {copyButton}
            </Flex>
          )}
          {title}
          {showCloseButton && (
            <ModalCloseButton {...closeButtonProps} />
          )}
        </ModalHeader>
        <ModalBody flexDir="column">
          {children}
        </ModalBody>
        {footer && (
          <ModalFooter>
            {footer}
          </ModalFooter>
        )}
      </ModalContent>
    </ChakraModal>
  );
};

export default Modal;
